import { z } from "zod";

const toTrimmedString = (value) => String(value ?? "").trim();
const emptyToUndefined = (value) => {
  const str = toTrimmedString(value);
  return str.length ? str : undefined;
};
const normalizeEmail = (value) => toTrimmedString(value).toLowerCase();
const normalizePhone = (value) => toTrimmedString(value).replace(/\D/g, "").slice(0, 10);
const emptyToNull = (value) => {
  if (value === undefined || value === null) return null;
  const str = toTrimmedString(value);
  return str.length ? value : null;
};
const toPositiveInt = (message) =>
  z.preprocess((value) => Number(value), z.number().int().positive(message));

export const zodErrorToFieldErrors = (zodError) => {
  const out = {};
  const fieldErrors = zodError?.flatten?.().fieldErrors || {};
  Object.keys(fieldErrors).forEach((key) => {
    const first = fieldErrors[key]?.[0];
    if (first) out[key] = first;
  });
  return out;
};

export const zodErrorToFirstMessage = (zodError, fallback = "Please fix validation errors.") =>
  zodError?.issues?.[0]?.message || fallback;

export const loginSchema = z.object({
  email: z.preprocess(normalizeEmail, z.string().email("Please enter a valid email address.")),
  password: z.preprocess(toTrimmedString, z.string().min(1, "Password is required.")),
  society_id: z.preprocess(emptyToUndefined, z.string().optional()),
});

export const resetPasswordSchema = z
  .object({
    user_id: toPositiveInt("Invalid or missing reset link."),
    token: z.preprocess(toTrimmedString, z.string().min(1, "Invalid or missing reset link.")),
    password: z.preprocess(toTrimmedString, z.string().min(6, "Password must be at least 6 characters.")),
    confirm: z.preprocess(toTrimmedString, z.string().min(1, "Confirm password is required.")),
  })
  .refine((data) => data.password === data.confirm, {
    path: ["confirm"],
    message: "Passwords do not match.",
  });

export const supportTicketSchema = z.object({
  subject: z.preprocess(toTrimmedString, z.string().min(1, "Subject is required").max(255)),
  description: z.preprocess(toTrimmedString, z.string().optional()).default(""),
});

export const visitorEntrySchema = z.object({
  society_id: toPositiveInt("Select society"),
  tower_id: toPositiveInt("Select tower"),
  unit_id: toPositiveInt("Select unit"),
  resident_user_id: z.preprocess(emptyToNull, z.coerce.number().int().positive().nullable()).default(null),
  visitor_name: z.preprocess(toTrimmedString, z.string().min(1, "Visitor name required")),
  visitor_phone: z.preprocess(
    normalizePhone,
    z.string().regex(/^\d{10}$/, "Enter a valid 10-digit mobile number")
  ),
  visitor_type_id: z.preprocess(emptyToNull, z.coerce.number().int().positive().nullable()).default(null),
  purpose: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  vehicle_number: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  no_of_visitors: z.coerce.number().int().min(1).max(99).default(1),
  remarks: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  user_id: z.preprocess(emptyToUndefined, z.coerce.number().int().positive().optional()),
});

const inviteGuestSchema = z.object({
  guest_name: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  mobile_number: z.preprocess(
    (value) => {
      const phone = normalizePhone(value);
      return phone || undefined;
    },
    z.string().regex(/^\d{10}$/, "Guest mobile number must be 10 digits").optional()
  ),
  vehicle_number: z.preprocess(toTrimmedString, z.string().optional()).default(""),
});

export const createInviteSchema = z.object({
  society_id: toPositiveInt("Select society"),
  tower_id: toPositiveInt("Select tower"),
  unit_id: toPositiveInt("Select unit"),
  invite_type: z.enum(["guest", "cab", "delivery"]),
  invite_sub_type: z.string().min(1),
  title: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  visitor_name: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  mobile_number: z.preprocess(
    (value) => {
      const phone = normalizePhone(value);
      return phone || undefined;
    },
    z.string().regex(/^\d{10}$/, "Mobile number must be 10 digits").optional()
  ),
  company_name: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  vehicle_number: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  purpose: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  valid_from: z.preprocess(toTrimmedString, z.string().min(1, "valid_from required")),
  valid_to: z.preprocess(toTrimmedString, z.string().min(1, "valid_to required")),
  start_time: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  end_time: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  entries_per_day: z.coerce.number().int().min(1).max(99).default(1),
  max_guest_count: z.coerce.number().int().min(1).max(500).default(10),
  allowed_days: z.array(z.string()).default([]),
  is_private: z.coerce.boolean().default(false),
  approval_required: z.coerce.boolean().default(false),
  guests: z.array(inviteGuestSchema).default([]),
});

export const societyAdminSchema = ({ passwordRequired = false } = {}) =>
  z
    .object({
      society_admin_name: z.preprocess(toTrimmedString, z.string().min(1, "Society admin name is required.")),
      society_id: toPositiveInt("Please select a society."),
      email: z.preprocess(normalizeEmail, z.string().email("Please enter a valid email address.")),
      phone: z.preprocess(normalizePhone, z.string().regex(/^\d{10}$/, "Phone number must be exactly 10 digits.")),
      password_hash: z.preprocess(toTrimmedString, z.string().optional()).default(""),
    })
    .superRefine((data, ctx) => {
      if (passwordRequired && !data.password_hash) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["password_hash"],
          message: "Password is required.",
        });
      }
    });

export const societyServiceSchema = z.object({
  service: z.preprocess(toTrimmedString, z.string().min(1, "Service is required.")),
  society_name: z.preprocess(toTrimmedString, z.string().min(1, "Society name is required.")),
});

export const societyProviderSchema = z.object({
  service_provider: z.preprocess(toTrimmedString, z.string().min(1, "Service provider is required.")),
  society_name: z.preprocess(toTrimmedString, z.string().min(1, "Society name is required.")),
  phone: z.preprocess(normalizePhone, z.string().regex(/^\d{10}$/, "Phone number must be exactly 10 digits.")),
  select_service: z.preprocess(toTrimmedString, z.string().min(1, "Service is required.")),
  description: z.preprocess(toTrimmedString, z.string().min(1, "Description is required.")),
  specilities: z.preprocess(toTrimmedString, z.string().min(1, "Specialties are required.")),
  timings: z.array(
    z.object({
      day: z.string().min(1),
      enabled: z.boolean(),
      start: z.string().min(1),
      end: z.string().min(1),
    })
  ),
});

const towerItemSchema = z.union([
  z.object({
    tower_id: z.coerce.number().int().positive().optional(),
    tower_name: z.preprocess(
      (v) => (typeof v === "string" ? v.trim() : ""),
      z.string().min(1, "Tower name cannot be empty")
    ),
  }),
  z.preprocess(
    (v) => (typeof v === "string" ? v.trim() : undefined),
    z.string().min(1, "Tower name cannot be empty")
  ),
]);

export const towerListSchema = z.object({
  society_id: toPositiveInt("Society is required."),
  towers: z.array(towerItemSchema).min(1, "Add at least one tower"),
});

export const unitGenerationSchema = z.object({
  tower_id: toPositiveInt("Tower is required."),
  total_floors: z.coerce.number().int().positive("Enter valid total floors"),
  units_per_floor: z.coerce.number().int().min(0, "Units per floor cannot be negative").default(0),
});

export const residentImportSchema = z.object({
  file: z.any().refine(Boolean, "Please choose a CSV/XLSX file."),
  societyId: z.preprocess(emptyToUndefined, z.string().optional()),
  dryRun: z.boolean(),
});

export const unitImportSchema = z.object({
  file: z.any().refine(Boolean, "Please select a file"),
  societyId: toPositiveInt("Please select a society before importing"),
});

export const rejectionReasonSchema = z.object({
  rejectionReason: z.preprocess(toTrimmedString, z.string().min(1, "Rejection reason required")),
});

export const roleFormSchema = z.object({
  name: z.preprocess(toTrimmedString, z.string().min(1, "Role name is required.")),
  description: z.preprocess(toTrimmedString, z.string().optional()).default(""),
  power: z.coerce.number().int().min(1, "Power must be at least 1").max(100, "Power cannot exceed 100").default(50),
  scope: z.preprocess(toTrimmedString, z.string().optional()).default("resident"),
  propertyTypes: z.array(z.string()).default([]),
  assignmentLevel: z.preprocess(emptyToUndefined, z.enum(["Flat", "Tower", "Property"]).optional()),
});

export const passwordResetRequestSchema = z.object({
  email: z.preprocess(normalizeEmail, z.string().email("Please enter a valid email address.")),
  society_id: z.preprocess(emptyToUndefined, z.string().optional()),
});
